import { useAtomValue } from "jotai";
import type { ReactNode } from "react";

import { Button, EmptyState, Panel, TypingDots } from "@go-novus/ui";

import { isTenantReady } from "#/entities/tenant";
import * as m from "#/paraglide/messages";
import { readBackendFailure } from "#/shared/lib/backend-api";
import { boundShopDomain } from "#/shared/lib/bound-store";
import { openShopifyAdmin } from "#/shared/lib/shopify-admin";

import {
  getStoreConnectionOperation,
  type GetStoreConnectionOperation,
} from "../api/get-store-connection";
import { storeConnectionAtom } from "../query/store-connection-query";
import { readStoreConnectionFailureMessage } from "./store-connection-failure";

interface GateScreenProps {
  children: ReactNode;
}

/** 三种拦截态共用的整屏底：业务页面还没挂上，这里是唯一有内容的地方。 */
function GateScreen({ children }: GateScreenProps) {
  return (
    <main className="flex min-h-screen items-center justify-center bg-[var(--bg-page-body)] p-4">
      {children}
    </main>
  );
}

/** 全站只在服务端确认 ready 后挂载业务页面，准备中不丢弃有效登录态。 */
export function StoreConnectionGate({ children }: { children: ReactNode }) {
  const connection = useAtomValue(storeConnectionAtom);

  if (connection.data && isTenantReady(connection.data)) {
    return children;
  }

  // 还在读状态时整屏没有可占位的框，摆一张白卡片，里面是挂件那三个点：它本来就是
  // 这套产品里表示「等一下」的样子，也是全站唯一一个循环动画。
  // 尺寸照 EmptyState 的图标卡片，两种拦截态在灰底上是同一块白方块。
  if (connection.isPending) {
    return (
      <GateScreen>
        <div role="status">
          <span className="sr-only">{m.home_store_connection_loading()}</span>
          <Panel className="grid h-14 w-14 place-items-center text-[var(--content-fg-subtle)]">
            <TypingDots label={m.home_store_connection_loading()} />
          </Panel>
        </div>
      </GateScreen>
    );
  }

  // 401 是「这家店的登录没了」：刷新救不回来，动作是回 Shopify 重新打开；
  // 其余失败保留刷新重试。准备中同样是终局态：给一句话和一个重试。
  const failure = connection.isError
    ? readBackendFailure<GetStoreConnectionOperation>(connection.error, [
        getStoreConnectionOperation,
      ])
    : null;
  const expired = failure?.status === 401;
  const shop = boundShopDomain();
  return (
    <GateScreen>
      <div aria-live="polite">
        <EmptyState
          heading={
            expired
              ? m.store_connection_expired_heading()
              : connection.isError
                ? m.store_connection_error_heading()
                : m.store_connection_preparing_heading()
          }
          body={
            connection.isError
              ? (readStoreConnectionFailureMessage(connection.error) ??
                m.store_connection_failure_unexpected())
              : m.store_connection_preparing_body()
          }
          action={
            expired && shop !== null ? (
              <Button
                onClick={() => {
                  openShopifyAdmin(shop);
                }}
              >
                {m.console_store_open_in_shopify()}
              </Button>
            ) : (
              <Button
                onClick={() => {
                  window.location.reload();
                }}
              >
                {m.store_connection_refresh()}
              </Button>
            )
          }
        />
      </div>
    </GateScreen>
  );
}
